/**
 * Projects — SAP backend implementation.
 *
 * Part of the isolated `sap-backend/` side project (see ../README.md). Backs the
 * dataService `getProjects(portfolioId?)` contract with project headers from SAP
 * PS (PROJ), exposed by the custom OData V4 service ZUI_M12_PROJECT_O4
 * (CDS ZC_M12_PROJECT, package ZMACH12, built this phase).
 *
 * SCOPE / FIDELITY NOTES (honest about what S/4 can and cannot give):
 *  - PROJECT contract is rich (status, EVM, budget, risk, health, portfolio).
 *    PROJ supplies id/name/dates/responsible/profile/company-code. Everything
 *    else is derived/forecast data that lives outside the project definition, so
 *    it is filled with safe defaults here (budget zero, status 'In Progress',
 *    health 'Green', etc.) — same approach as the WBS structure read.
 *  - S/4 has NO portfolio concept, so `portfolioId` is ignored and ALL projects
 *    are returned (capped). In sap mode the portfolio dimension doesn't map; the
 *    project list IS the set of S/4 projects (the system of record).
 */

import type { Project } from '@/types/ppm'
import { S4_PROXY_BASE } from '../config'
import { odataGetCollection } from '../odataClient'

/** Runtime path uses `srvd` (not the published `srvd_a2x`) — same kernel quirk
 *  as every other Mach12 V4 service (see currencyRatesService.ts). */
const PROJECT_PATH = `${S4_PROXY_BASE}/zui_m12_project_o4/srvd/sap/z_m12_project_sd/0001/Project`

/** Row shape from ZC_M12_PROJECT (see its $metadata). Dates come as
 *  'YYYY-MM-DD' or null; ResponsibleNo is NUMC ('0' when unset). */
interface RawProjectRow {
  ProjectID: string
  ProjectName: string
  PlannedStart: string | null
  PlannedFinish: string | null
  CompanyCode: string
  Plant: string
  ResponsibleNo: string
  ProjectProfile: string
  ControllingArea: string
}

/**
 * Read project headers from SAP PS. `portfolioId` is ignored (no S/4 portfolio
 * concept); all projects are returned, capped at 500.
 */
export async function getProjects(_portfolioId?: string): Promise<Project[]> {
  const url = `${PROJECT_PATH}?$top=500&$orderby=ProjectID`
  const rows = await odataGetCollection<RawProjectRow>(url)

  return rows.map((r): Project => ({
    id: r.ProjectID,
    code: r.ProjectID,
    name: r.ProjectName,
    description: '',
    portfolioId: '', // S/4 has no portfolio concept
    projectManager: r.ResponsibleNo && r.ResponsibleNo !== '0' ? r.ResponsibleNo : '',
    projectManagerName: '',
    status: 'In Progress', // PS system status (JEST/TJ02) not read here; safe default
    startDate: r.PlannedStart ?? '',
    plannedEndDate: r.PlannedFinish ?? '',
    actualEndDate: null,
    // Financial/EVM truth is not on the project definition — defaults mirror the
    // app's zero shape; actuals come from ACDOCA via getPeriodicActuals.
    budget: { total: 0, spent: 0, committed: 0, available: 0, currency: 'USD' },
    schedule: { percentComplete: 0, spi: 0, cpi: 0, scheduleVarianceDays: 0 },
    schedulingMethod: 'traditional',
    healthStatus: 'Green',
    evmEnabled: false,
    riskScore: 0,
    businessOwner: '',
    priority: 'Medium',
    projectType: r.ProjectProfile || '', // PS project profile as the type label
    fundingType: 'Internal',
    contractId: null,
    wbsElements: [],
  }))
}
